/**
 * Raíz del ERP.
 *
 * Mientras no se sabe si hay sesión, no se pinta ni el login ni el
 * escritorio: enseñar el formulario para quitarlo medio segundo después es
 * la forma más rápida de que alguien empiece a teclear su contraseña en
 * una pantalla que va a desaparecer.
 */

import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useSession } from './session/session-context.js';
import { LoginScreen } from './screens/login.js';
import { PendingScreen } from './screens/pending.js';
import { CustomersScreen } from './screens/customers.js';
import { EquipmentScreen } from './screens/equipment.js';
import { WorkOrdersScreen } from './screens/work-orders.js';
import { SitesScreen } from './screens/sites.js';
import { ContractsScreen } from './screens/contracts.js';
import { PlanningScreen } from './screens/planning.js';
import { WorkshopScreen } from './screens/workshop.js';
import { StockScreen } from './screens/stock.js';
import { QuotesScreen } from './screens/quotes.js';
import { DeliveryNotesScreen } from './screens/delivery-notes.js';
import { PropertyManagersScreen } from './screens/property-managers.js';
import { ProformasScreen } from './screens/proformas.js';
import { ContractDetailScreen } from './screens/detail/contract-detail.js';
import { AnomalyDetailScreen } from './screens/detail/anomaly-detail.js';
import { DeliveryNoteDetailScreen } from './screens/detail/delivery-note-detail.js';
import { InvoicesScreen } from './screens/invoices.js';
import { PurchaseOrdersScreen } from './screens/purchase-orders.js';
import { OpportunitiesScreen } from './screens/opportunities.js';
import { RenewalRadarScreen } from './screens/renewal-radar.js';
import { CommissionsScreen } from './screens/commissions.js';
import { PoolsScreen } from './screens/pools.js';
import { RentalsScreen } from './screens/rentals.js';
import { InspectionsScreen } from './screens/inspections.js';
import { AnomaliesScreen } from './screens/anomalies.js';
import { MovementsScreen } from './screens/movements.js';
import { RegulationScreen } from './screens/regulation.js';
import { DashboardScreen } from './screens/dashboard.js';
import { CollectionsScreen } from './screens/collections.js';
import { ReportsScreen } from './screens/reports.js';
import { UsersScreen } from './screens/users.js';
import { DevicesScreen } from './screens/devices.js';
import { WarehouseAdminScreen } from './screens/warehouse-admin.js';
import { AssetCatalogScreen } from './screens/asset-catalog.js';
import { QrResolveScreen } from './screens/qr-resolve.js';
import { AccountScreen } from './screens/account.js';
import { CustomerFormScreen } from './screens/forms/customer-form.js';
import { SiteFormScreen } from './screens/forms/site-form.js';
import { EquipmentFormScreen } from './screens/forms/equipment-form.js';
import { EquipmentEditScreen } from './screens/forms/equipment-edit.js';
import { ContractFormScreen } from './screens/forms/contract-form.js';
import { WorkOrderFormScreen } from './screens/forms/work-order-form.js';
import { WorkOrderEditScreen } from './screens/forms/work-order-edit.js';
import { QuoteFormScreen } from './screens/forms/quote-form.js';
import { DeliveryNoteFormScreen } from './screens/forms/delivery-note-form.js';
import { InvoiceFormScreen } from './screens/forms/invoice-form.js';
import { CompanySettingsScreen } from './screens/company-settings.js';
import { CustomerDetailScreen } from './screens/detail/customer-detail.js';
import { SiteDetailScreen } from './screens/detail/site-detail.js';
import { EquipmentDetailScreen } from './screens/detail/equipment-detail.js';
import { InspectionRunScreen } from './screens/inspection-run.js';
import { AppShell } from './shell/app-shell.js';
import { NAVIGATION } from './shell/navigation.js';

/** Rutas que ya tienen pantalla de verdad. */
const BUILT = new Set([
  '/clientes',
  '/equipos',
  '/ordenes',
  '/centros',
  '/contratos',
  '/planificacion',
  '/taller',
  '/existencias',
  '/presupuestos',
  '/albaranes',
  '/administradores',
  '/proformas',
  '/facturas',
  '/compras',
  '/oportunidades',
  '/radar',
  '/comisiones',
  '/parques',
  '/alquileres',
  '/revisiones',
  '/anomalias',
  '/movimientos',
  '/normativa',
  '/',
  '/cobros',
  '/informes',
  '/configuracion',
]);

export function App(): React.ReactNode {
  const { user } = useSession();

  if (user === undefined) {
    return <div className="booting">Comprobando la sesión…</div>;
  }

  if (user === null) {
    return <LoginScreen />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/clientes" element={<CustomersScreen />} />
          <Route path="/usuarios" element={<UsersScreen />} />
          <Route path="/terminales" element={<DevicesScreen />} />
          <Route path="/almacenes" element={<WarehouseAdminScreen />} />
          <Route path="/catalogo" element={<AssetCatalogScreen />} />
          <Route path="/q/:token" element={<QrResolveScreen />} />
          <Route path="/mi-cuenta" element={<AccountScreen />} />
          <Route path="/clientes/nuevo" element={<CustomerFormScreen />} />
          <Route path="/clientes/:id" element={<CustomerDetailScreen />} />
          <Route path="/clientes/:id/editar" element={<CustomerFormScreen />} />
          <Route path="/centros/nuevo" element={<SiteFormScreen />} />
          <Route path="/centros/:id" element={<SiteDetailScreen />} />
          <Route path="/centros/:id/editar" element={<SiteFormScreen />} />
          <Route path="/equipos/nuevo" element={<EquipmentFormScreen />} />
          <Route path="/equipos/:id/editar" element={<EquipmentEditScreen />} />
          <Route path="/equipos/:id" element={<EquipmentDetailScreen />} />
          <Route path="/contratos/nuevo" element={<ContractFormScreen />} />
          <Route path="/ordenes/nueva" element={<WorkOrderFormScreen />} />
          <Route path="/ordenes/:id" element={<WorkOrderEditScreen />} />
          <Route path="/presupuestos/nuevo" element={<QuoteFormScreen />} />
          <Route path="/albaranes/nuevo" element={<DeliveryNoteFormScreen />} />
          <Route path="/albaranes/:id" element={<DeliveryNoteDetailScreen />} />
          <Route path="/facturas/nueva" element={<InvoiceFormScreen />} />
          <Route path="/facturas/:id" element={<InvoiceFormScreen />} />
          <Route path="/equipos" element={<EquipmentScreen />} />
          <Route path="/ordenes" element={<WorkOrdersScreen />} />
          <Route path="/centros" element={<SitesScreen />} />
          <Route path="/contratos" element={<ContractsScreen />} />
          <Route path="/planificacion" element={<PlanningScreen />} />
          <Route path="/taller" element={<WorkshopScreen />} />
          <Route path="/existencias" element={<StockScreen />} />
          <Route path="/presupuestos" element={<QuotesScreen />} />
          <Route path="/albaranes" element={<DeliveryNotesScreen />} />
          <Route path="/administradores" element={<PropertyManagersScreen />} />
          <Route path="/proformas" element={<ProformasScreen />} />
          <Route path="/contratos/:id" element={<ContractDetailScreen />} />
          <Route path="/facturas" element={<InvoicesScreen />} />
          <Route path="/compras" element={<PurchaseOrdersScreen />} />
          <Route path="/oportunidades" element={<OpportunitiesScreen />} />
          <Route path="/radar" element={<RenewalRadarScreen />} />
          <Route path="/comisiones" element={<CommissionsScreen />} />
          <Route path="/parques" element={<PoolsScreen />} />
          <Route path="/alquileres" element={<RentalsScreen />} />
          <Route path="/revisiones" element={<InspectionsScreen />} />
          <Route path="/revisiones/:id" element={<InspectionRunScreen />} />
          <Route path="/anomalias" element={<AnomaliesScreen />} />
          <Route path="/anomalias/:id" element={<AnomalyDetailScreen />} />
          <Route path="/movimientos" element={<MovementsScreen />} />
          <Route path="/normativa" element={<RegulationScreen />} />
          <Route path="/cobros" element={<CollectionsScreen />} />
          <Route path="/informes" element={<ReportsScreen />} />
          <Route path="/configuracion" element={<CompanySettingsScreen />} />
          <Route path="/" element={<DashboardScreen />} />

          {/* El resto de secciones avisan de que están sin construir. */}
          {NAVIGATION.flatMap((group) =>
            group.items
              .filter((item) => !BUILT.has(item.path))
              .map((item) => (
                <Route
                  key={item.path}
                  path={item.path}
                  element={<PendingScreen title={item.label} group={group.label} />}
                />
              )),
          )}
          <Route path="*" element={<PendingScreen title="Sección no encontrada" group="" />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
